import { purple } from "@mui/material/colors";
import React, { useContext, useEffect, useMemo, useState } from "react";
import useMeasure from "react-use-measure";
import styled from "styled-components";
import {
  VictoryChart,
  VictoryLine,
  VictoryAxis,
  VictoryLegend,
  VictoryVoronoiContainer,
  VictoryTooltip,
  createContainer,
} from "victory";
import { UserContext } from "../../../contexts/UserContext";

const StyledChart = styled.div`
  position: relative; /* so child overlay can position absolutely */
  width: 100%;
  height: 100%;
  padding: 1rem;
`;



function SumOccupiedByHeight({ dataset, valueMode }) {
  const VictoryZoomVoronoiContainer = createContainer("zoom", "voronoi");
  const [ref, bounds] = useMeasure();
  const [measuredWidth, setMeasuredWidth] = useState(null);
  const chartWidth = measuredWidth?? 800;
  const chartHeight = chartWidth / 2.2;
  const isPercentMode = valueMode === "pct";
  
  const { userDetails } = useContext(UserContext);

  const allowZoom = userDetails?.userId != null;

  console.log("Dataset", dataset)


  useEffect(() => {
    if (bounds.width > 0 && measuredWidth === null) {
      setMeasuredWidth(bounds.width);
    }
  }, [bounds.width, measuredWidth]);

  const {
    series01,
    series02,
    series03,
    series04,
    capacity01,
    capacity02,
    capacity03,
    capacity04,
    labels,
  } = useMemo(() => {
    if (!dataset || dataset.length === 0) {
      return {
        series01: [],
        series02: [],
        series03: [],
        series04: [],
        capacity01: [],
        capacity02: [],
        capacity03: [],
        capacity04: [],
        labels: ["Level 01", "Level 02", "Level 03", "Level 04"],
      };
    }

    const first = dataset[0];

    const labels = [
      first.locationDescription01?.split(" ")[2] || "Level 01",
      first.locationDescription02?.split(" ")[2] || "Level 02",
      first.locationDescription03?.split(" ")[2] || "Level 03",
      first.locationDescription04?.split(" ")[2] || "Level 04",
    ];


const occupiedFields = {
  one: {
    occupied: "locationOccupied01",
    capacity: "locationCount01",
  },
  two: {
    occupied: "locationOccupied02",
    capacity: "locationCount02",
  },
  three: {
    occupied: "locationOccupied03",
    capacity: "locationCount03",
  },
  four: {
    occupied: "locationOccupied04",
    capacity: "locationCount04",
  },
};

const mapOccupiedSeries = ({ occupied, capacity }, seriesLabel) =>
  dataset.map((d) => {
    const occupiedValue = d[occupied] ?? 0;
    const capacityValue = d[capacity] ?? 0;

    const percentValue =
      capacityValue > 0 ? (occupiedValue / capacityValue) * 100 : 0;

    return {
      x: new Date(d.serverTime),
      y: isPercentMode ? percentValue : occupiedValue,

      rawValue: occupiedValue,
      percentValue,
      capacityValue,
      seriesLabel,
    };
  });

const series01 = mapOccupiedSeries(occupiedFields.one, labels[0]);
const series02 = mapOccupiedSeries(occupiedFields.two, labels[1]);
const series03 = mapOccupiedSeries(occupiedFields.three, labels[2]);
const series04 = mapOccupiedSeries(occupiedFields.four, labels[3]);

const mapCapacityFromSeries = (series) =>
  series.map((point) => ({
    x: point.x,
    y: point.capacityValue ?? 0,
    seriesLabel: point.seriesLabel,
  }));

return {
  series01,
  series02,
  series03,
  series04,

  capacity01: mapCapacityFromSeries(series01),
  capacity02: mapCapacityFromSeries(series02),
  capacity03: mapCapacityFromSeries(series03),
  capacity04: mapCapacityFromSeries(series04),

  labels,
};

  }, [dataset, isPercentMode]);

    const tickValues = useMemo(() => {
      if (!dataset?.length) return [];

      const start = new Date(dataset[0].serverTime);
      const end = new Date(dataset[dataset.length - 1].serverTime);

      const rangeMs = end - start;
      const rangeDays = rangeMs / (1000 * 60 * 60 * 24);

      // ✅ SMALL RANGE → use daily ticks (no duplicates)
      if (rangeDays <= 10) {
        const ticks = [];
        const current = new Date(start);
        current.setHours(0, 0, 0, 0);

        while (current <= end) {
          ticks.push(new Date(current));
          current.setDate(current.getDate() + 1);
        }

        return ticks;
      }      

      // ✅ LARGE RANGE → limit to 10 evenly spaced ticks
      const tickCount = 10;
      const step = rangeMs / (tickCount - 1);

      return Array.from(
        { length: tickCount },
        (_, i) => new Date(start.getTime() + i * step)
      );
    }, [dataset]);

    
    const yDomain = isPercentMode ? [0, 100] : [0, 500];

    const yTickValues = isPercentMode
      ? [0, 20, 40, 60, 80, 100]
      : [0, 100, 200, 300, 400, 500];


  if (!dataset || dataset.length === 0) {
    return <div style={{ padding: "1rem" }}>No data</div>;
  }

  return (
    <StyledChart>
      <VictoryChart
        scale={{ x: "time" }}
        width={chartWidth}
        height={chartHeight}
        domain={{ y: yDomain }}
        domainPadding={5}
        containerComponent={
          <VictoryZoomVoronoiContainer
            zoomDimension="x"
            allowZoom={allowZoom}
            allowPan={allowZoom}
            mouseFollowTooltips
            voronoiBlacklist={[
              "capacity01",
              "capacity02",
              "capacity03",
              "capacity04",
            ]}
            labels={({ datum }) => {
              const timeLabel = new Date(datum.x).toLocaleString([], {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              });  
              return isPercentMode
                ? `${datum.seriesLabel}
              ${timeLabel}
              Occupied: ${Number(datum.percentValue).toFixed(1)}%`
                : `${datum.seriesLabel}
              ${timeLabel}
              Occupied: ${datum.rawValue}
              Capacity: ${datum.capacityValue}`;
            }}
            labelComponent={
              <VictoryTooltip
                flyoutStyle={{
                  fill: "rgba(10, 15, 25, 0.95)",
                  stroke: "#666",
                  strokeWidth: 1,
                }}
                style={{
                  fill: "#eee",
                  fontSize: 10,
                }}
                cornerRadius={6}
                pointerLength={6}
              />
            }
          />
        }
      >
        {/* X AXIS */}
        <VictoryAxis
          tickValues={tickValues}
          tickFormat={(t) =>
            new Date(t).toLocaleDateString([], {
              day: "2-digit",
              month: "short",
            })
          }
          style={{
            tickLabels: { fill: "#ccc", fontSize: 10 },
            axis: { stroke: "#ccc" },
            grid: { stroke: "#444", strokeDasharray: "4" },
          }}
        />

        {/* Y AXIS */}
        <VictoryAxis
          dependentAxis
          tickValues={yTickValues}
          tickFormat={(t) => (isPercentMode ? `${t}%` : t)}
          style={{
            tickLabels: { fill: "#ccc", fontSize: 10 },
            axis: { stroke: "#ccc" },
            grid: { stroke: "#444", strokeDasharray: "4" },
          }}
        />

        {/* LINES */}      
          
            {!isPercentMode && (<VictoryLine
              name="capacity01"
              data={capacity01}
              interpolation="monotoneX"
              style={{
                data: {
                  stroke: "var(--crane-height-low)",
                  strokeWidth: 1.5,
                  opacity: 0.35,
                  strokeDasharray: "5,8",
                },
              }}
            />)}

             {!isPercentMode && (<VictoryLine
              name="capacity02"
              data={capacity02}
              interpolation="monotoneX"
              style={{
                data: {
                  stroke: "var(--crane-height-md)",
                  strokeWidth: 1.5,
                  opacity: 0.35,
                  strokeDasharray: "5,8",
                },
              }}
            />)}

             {!isPercentMode && (<VictoryLine
              name="capacity03"
              data={capacity03}
              interpolation="monotoneX"
              style={{
                data: {
                  stroke: "var(--crane-height-hi)",
                  strokeWidth: 1.5,
                  opacity: 0.35,
                  strokeDasharray: "5,8",
                },
              }}
            />)}

             {!isPercentMode && (<VictoryLine
              name="capacity04"
              data={capacity04}
              interpolation="monotoneX"
              style={{
                data: {
                  stroke: "var(--crane-height-top)",
                  strokeWidth: 1.5,
                  opacity: 0.35,
                  strokeDasharray: "5,8",
                },
              }}
            />)}         
       

        <VictoryLine
          data={series01}
          interpolation="monotoneX"
          style={{
            data: { stroke: "var(--crane-height-low)", strokeWidth: 2 },
          }}
        />

        <VictoryLine
          data={series02}
          interpolation="monotoneX"
          style={{
            data: { stroke: "var(--crane-height-md)", strokeWidth: 2 },
          }}
        />

        <VictoryLine
          data={series03}
          interpolation="monotoneX"
          style={{ data: { stroke: "var(--crane-height-hi)", strokeWidth: 2 } }}
        />

        <VictoryLine
          data={series04}
          interpolation="monotoneX"
          style={{
            data: { stroke: "var(--crane-height-top)", strokeWidth: 2 },
          }}
        />

        {/* LEGEND */}
        <VictoryLegend
          x={80}
          y={0}
          orientation="horizontal"
          gutter={20}
          style={{ labels: { fill: "#ccc", fontSize: 10 } }}
          data={[
            { name: labels[0], symbol: { fill: "var(--crane-height-low)" } },
            { name: labels[1], symbol: { fill: "var(--crane-height-md)" } },
            { name: labels[2], symbol: { fill: "var(--crane-height-hi)" } },
            { name: labels[3], symbol: { fill: "var(--crane-height-top)" } },
          ]}
        />
      </VictoryChart>
    </StyledChart>
  );
}

export default SumOccupiedByHeight;
